import { CUSTOM_FIELD_TYPES } from '../models/dto/custom-fields.dto';
import type { FormFieldEntity } from '../models/entity/form-field.entity';
import type { FormLayoutModel } from './form-layout';

/**
 * Workd Forms — the vocabulary shared by crm-api, the CRM and the storefront
 * (docs/claude/forms/01-design.md). Pure types and constants only, so this file
 * runs unchanged in a browser and in Node.
 */

/**
 * Form field types: the client custom field types plus two that only make sense on a
 * form. Reusing the custom field list keeps one field-type vocabulary in Workd, so the
 * shared inputs render custom fields and form fields alike.
 *
 * - `consent`: a checkbox whose exact label is part of the form version, so a consent
 *   record can always show what was agreed to.
 * - `hidden`: a value the page sets rather than the visitor (a product id, a campaign
 *   code). Never shown, still validated.
 * - `signature` (PD-16691): a signature drawn on a pad, stored as a PNG data URL
 *   (form-signature.ts). Forms only: a client custom field never holds one.
 * - `teamMember` (PD-16691): "choose a person", such as the visitor's account manager.
 *   Its choices are the tenant's active users in chosen roles plus chosen people
 *   (`choiceSource`), worked out each time the form is drawn; the answer is the user id.
 * - `file` (PD-16713): documents the visitor attaches (a license, a resale certificate).
 *   Each file is uploaded on its own as it is picked (form-files.ts); the page sends the
 *   upload ids, and the stored answer is the files' names. The files themselves stay with
 *   the submission (`form_submission_file`).
 * - `table` (PD-13768): rows with fixed columns, filled by staff before a form is sent
 *   (equipment placed, products and counts, agreed prices). Its answer is the rows, each
 *   a set of cell values keyed by column (sent-forms.ts). Always locked for the customer.
 * - `paymentMethod` (PD-13768): which of the client's saved cards or bank accounts the
 *   customer chooses (or adds), or "Bill To" when staff allowed it. The answer is only
 *   the saved method's id (`method:<id>`) or `bill_to`: never a card or account number.
 */
export const FORM_FIELD_TYPES = [
	...CUSTOM_FIELD_TYPES,
	'consent',
	'hidden',
	'signature',
	'teamMember',
	'file',
	'table',
	'paymentMethod',
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/**
 * What a field says about the submitter. Matching, the inbox and default action
 * mappings read this instead of guessing from keys or labels.
 */
export const FORM_FIELD_IDENTITIES = [
	'first_name',
	'last_name',
	'full_name',
	'email',
	'phone',
	'company',
	'message',
] as const;

export type FormFieldIdentity = (typeof FORM_FIELD_IDENTITIES)[number];

/** What a consent checkbox records agreement to. */
export const FORM_CONSENT_PURPOSES = [
	'accuracy',
	'terms',
	'processing',
	'marketing',
] as const;

export type FormConsentPurpose = (typeof FORM_CONSENT_PURPOSES)[number];

/** `registration` drives the storefront Register page; at most one active per brand. */
export const FORM_PURPOSES = ['registration', 'contact', 'custom'] as const;

export type FormPurpose = (typeof FORM_PURPOSES)[number];

/**
 * The key a page submits to. Capped at 32 characters because the key is sent to
 * Cloudflare Turnstile as the widget `action`, whose limit is 32.
 */
export const FORM_KEY_PATTERN = /^[a-z0-9_-]{1,32}$/;

/** Same rule as custom field keys: a letter first, then letters, digits and underscores. */
export const FORM_FIELD_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,59}$/;

export interface FormSelectOptionModel {
	value: string;
	label: string;
}

/**
 * Per-field limits an admin can set. `min` / `max` are numbers for number and currency
 * fields and `YYYY-MM-DD` strings for dates.
 *
 * PD-16713 — a file question's limits: which kinds of file it takes, the largest file in
 * megabytes and the most files. Each is optional; form-files.ts supplies the defaults and
 * the ranges an admin may choose from.
 */
export interface FormFieldValidationRulesModel {
	minLength?: number;
	maxLength?: number;
	pattern?: string;
	min?: number | string;
	max?: number | string;
	fileTypes?: Array<FormFileTypeGroup>;
	maxFileSizeMb?: number;
	maxFiles?: number;
}

/**
 * PD-16713 — the kinds of file a file question can take. `pdf` is PDF; `image` is JPEG, PNG
 * and WebP. Word and Excel are deliberately absent: without malware scanning only formats
 * Workd can check by their content (and re-encode, for pictures) are accepted.
 */
export const FORM_FILE_TYPE_GROUPS = ['pdf', 'image'] as const;

export type FormFileTypeGroup = (typeof FORM_FILE_TYPE_GROUPS)[number];

/** One cross-field rule exists: at least one of the named fields must have a value. */
export interface FormAtLeastOneRuleModel {
	rule: 'at_least_one';
	fieldKeys: Array<string>;
	/** Optional wording; a default is built from the fields' labels. */
	message?: string | null;
}

export type FormRuleModel = FormAtLeastOneRuleModel;

export const FORM_RULE_TYPES = ['at_least_one'] as const;

export interface FormBlocklistModel {
	emailDomains: Array<string>;
	emails: Array<string>;
	ips: Array<string>;
	keywords: Array<string>;
	/** Treat a link in any free-text answer as spam. */
	rejectLinks: boolean;
}

/**
 * `spamProtection` is `turnstile` for every tenant form. `honeypot_only` exists for
 * internal testing and is not offered in the settings screen.
 */
export interface FormSettingsModel {
	spamProtection: 'turnstile' | 'honeypot_only';
	/** A backstop behind Turnstile, high enough for a pharmacy chain behind one office IP. */
	rateLimitPerHour: number;
	duplicateWindowMinutes: number;
	blocklist: FormBlocklistModel;
}

export const DEFAULT_FORM_SETTINGS: Readonly<FormSettingsModel> = {
	spamProtection: 'turnstile',
	rateLimitPerHour: 20,
	duplicateWindowMinutes: 10,
	blocklist: {
		emailDomains: [],
		emails: [],
		ips: [],
		keywords: [],
		rejectLinks: false,
	},
};

/**
 * PD-16691 — whose names a `teamMember` question lists: the active users in these roles
 * (`_rbac_role` ids), plus these people (tenant user ids).
 */
export interface FormChoiceSourceModel {
	roles: Array<number>;
	users: Array<number>;
}

/**
 * PD-16730 — show a question only when an earlier choice question has one of these
 * answers (form-show-when.ts). `values` are the controlling question's option values; a
 * yes/no question's are `'true'` and `'false'`, and an unanswered yes/no counts as
 * `'false'`. For a multiple-answer question, any one of them ticked is enough.
 */
export interface FormShowWhenModel {
	fieldKey: string;
	values: Array<string>;
}

/**
 * The definition of one field as a form version stores it. Derived from the entity so a
 * column change reaches every consumer through the type checker.
 *
 * `choiceSource` is optional: only an account-manager question has one, and versions
 * saved before it existed (and the public definition, which never carries it) leave it
 * out. `showWhen` (PD-16730) is optional for the same reason: a version saved before
 * conditional questions existed has no such key, which reads as "always shown".
 * PD-13768 — the sent-form settings (`valueSource`, `customerAccess`, `writeBackTarget`,
 * `fieldOptions`) are optional for the same reason; missing reads as "no source, the
 * customer fills it, written nowhere".
 */
export type FormFieldDefinitionModel = Partial<
	Pick<
		FormFieldEntity,
		| 'choiceSource'
		| 'showWhen'
		| 'valueSource'
		| 'customerAccess'
		| 'writeBackTarget'
		| 'fieldOptions'
	>
> &
	Pick<
		FormFieldEntity,
		| 'fieldKey'
		| 'label'
		| 'adminLabel'
		| 'fieldType'
		| 'placeholder'
		| 'helpText'
		| 'isRequired'
		| 'sortOrder'
		| 'defaultValue'
		| 'selectOptions'
		| 'validationRules'
		| 'identity'
		| 'consentPurpose'
		| 'policyVersions'
		| 'termsBody'
		| 'termsUrl'
		| 'termsLinkText'
		| 'includeInNotification'
	>;

/**
 * Limits on a consent field's terms (PD-16691). The body is counted after sanitizing, as
 * stored; 20,000 characters holds a long commercial credit agreement. The address and the
 * link wording match their column lengths.
 */
export const FORM_TERMS_BODY_MAX_LENGTH = 20000;
export const FORM_TERMS_URL_MAX_LENGTH = 2048;
export const FORM_TERMS_LINK_TEXT_MAX_LENGTH = 200;

/**
 * What the validator needs: the active fields in display order, and the rules. The
 * renderer also reads `layout` (PD-16687); the validator never does, since a layout only
 * arranges the questions.
 */
export interface FormDefinitionModel {
	fields: ReadonlyArray<FormFieldDefinitionModel>;
	rules: ReadonlyArray<FormRuleModel>;
	layout?: FormLayoutModel | null;
}

/** Field-level error codes. Sites map them to their own wording next to the field. */
export const FORM_FIELD_ERROR_CODES = [
	'required',
	'invalid_type',
	'invalid_email',
	'invalid_phone',
	'invalid_number',
	'invalid_date',
	'invalid_datetime',
	'invalid_url',
	'invalid_option',
	'too_short',
	'too_long',
	'below_min',
	'above_max',
	'invalid_format',
	// PD-16713 — a file question: a file of a kind it does not take (judged by the file's
	// content, not its name), over its size limit, empty, or unreadable as the kind it
	// claims to be; more files than it allows; and an upload id Workd does not hold for
	// this form fill (never uploaded, removed after 24 hours, or another form's).
	'file_type_not_allowed',
	// Named or typed as a PDF or a picture the question takes, but the content is not one
	// (a program renamed invoice.pdf). Said plainly, because "Attach a PDF file" next to a
	// file called invoice.pdf reads as a bug (owner review).
	'file_not_valid_pdf',
	'file_not_valid_image',
	'file_too_large',
	'file_empty',
	'file_unreadable',
	'too_many_files',
	'upload_missing',
] as const;

export type FormFieldErrorCode = (typeof FORM_FIELD_ERROR_CODES)[number];

/** Form-level error codes returned by the public endpoint. */
export const FORM_ERROR_CODES = [
	'captcha_failed',
	'captcha_expired',
	'origin_not_allowed',
	'form_not_found',
	'rate_limited',
	// PD-16713 — the file upload endpoint: the first upload of a form fill must carry a
	// spam-check token (the page fetches one and sends again), and a request it cannot read.
	'upload_session_required',
	'upload_invalid',
] as const;

export type FormErrorCode = (typeof FORM_ERROR_CODES)[number];

export interface FormRuleErrorModel {
	rule: FormRuleModel['rule'];
	fieldKeys: Array<string>;
	message: string;
}

/**
 * PD-13768 — one row of a table question: each column's cell as text (numbers and prices
 * are written as plain decimals, `12.5`). Keyed by the column key.
 */
export type FormTableRowModel = Record<string, string>;

/**
 * A sanitized answer. Multiselect answers are arrays of strings; consent and boolean are
 * booleans; a table question's answer (PD-13768) is its rows.
 */
export type FormAnswerValue =
	| string
	| number
	| boolean
	| Array<string>
	| Array<FormTableRowModel>;

export interface FormValidationResultModel {
	valid: boolean;
	/**
	 * Cleaned answers keyed by field key; empty optional answers are left out, and so is
	 * any answer to a question its Show only when rule hides (PD-16730).
	 */
	answers: Record<string, FormAnswerValue>;
	errors: Record<string, FormFieldErrorCode>;
	ruleErrors: Array<FormRuleErrorModel>;
	/**
	 * PD-16730 — questions hidden by their Show only when rule for these answers, in field
	 * order. The renderer draws none of them; the validator never requires them.
	 */
	hiddenKeys: Array<string>;
}

// ─── Sent forms (PD-13768) ─────────────────────────────────────────────────

/**
 * PD-13768 — what the customer may do with a field on a sent form.
 *
 * - `fill`: the customer fills it (prefilled when the field has a source).
 * - `editable`: prefilled; the customer may change it. A change to a field that writes
 *   back becomes a proposed change staff accept or reject.
 * - `locked`: shown, read-only. The server ignores any value the page sends for it.
 * - `internal`: never shown to the customer, kept on the internal record.
 */
export const FORM_FIELD_CUSTOMER_ACCESS = [
	'fill',
	'editable',
	'locked',
	'internal',
] as const;

export type FormFieldCustomerAccess =
	(typeof FORM_FIELD_CUSTOMER_ACCESS)[number];

/** A table question's column (PD-13768). `item` cells hold an item id. */
export const FORM_TABLE_COLUMN_KINDS = [
	'text',
	'number',
	'currency',
	'item',
	'model',
] as const;

export type FormTableColumnKind = (typeof FORM_TABLE_COLUMN_KINDS)[number];

export interface FormTableColumnModel {
	/** Unique within the table; the key each row stores the cell under. */
	key: string;
	label: string;
	kind: FormTableColumnKind;
}

/**
 * Where a table's starting rows come from when the form is sent (Forms decision S4: named
 * row sources written in code). The sender can add, remove or edit rows before sending.
 *
 * - `client_item_prices`: one row per item in `itemIds`, with the client's price.
 * - `placed_merchandisers`: one row per merchandiser placed at the client.
 */
export const FORM_TABLE_ROW_SOURCES = [
	'client_item_prices',
	'placed_merchandisers',
] as const;

export type FormTableRowSource = (typeof FORM_TABLE_ROW_SOURCES)[number];

export interface FormTableOptionsModel {
	columns: Array<FormTableColumnModel>;
	rowSource?: FormTableRowSource | null;
	/** `client_item_prices`: the items listed, in order. */
	itemIds?: Array<number>;
	/** The most rows a sender may add. */
	maxRows?: number;
	/**
	 * The item each row is charged as when processing creates orders (an event's
	 * merchandiser rental, at the row's price). None: the rows are not charged.
	 */
	chargeItemId?: number;
}

/** The kinds of saved method a payment method question takes. */
export const FORM_PAYMENT_METHOD_KINDS = ['card', 'bank'] as const;

export type FormPaymentMethodKind = (typeof FORM_PAYMENT_METHOD_KINDS)[number];

export interface FormPaymentMethodOptionsModel {
	kinds: Array<FormPaymentMethodKind>;
	/**
	 * Whether the sender may choose Bill To terms (invoices paid directly), which makes
	 * the method optional unless the sender asks for one anyway.
	 */
	allowBillTo: boolean;
}

export const FORM_SIGNATURE_MODES = ['draw', 'type'] as const;

export type FormSignatureMode = (typeof FORM_SIGNATURE_MODES)[number];

/**
 * Choices the server works out each time the form is drawn, the way an account-manager
 * question's people are: the tenant's agreement term lengths (Settings), and the billing
 * terms the tenant supports (billing modes and terms codes).
 */
export const FORM_COMPUTED_CHOICES = ['term_lengths', 'billing_terms'] as const;

export type FormComputedChoices = (typeof FORM_COMPUTED_CHOICES)[number];

/** Values the server calculates from other answers when the form is sent. */
export const FORM_CALCULATIONS = ['event_total'] as const;

export type FormCalculation = (typeof FORM_CALCULATIONS)[number];

/**
 * PD-13768 — type-specific settings of a form field (`form_field.fieldOptions`). Every key
 * is optional and read through `normalizeFormFieldOptions`.
 */
export interface FormFieldOptionsModel {
	table?: FormTableOptionsModel;
	payment?: FormPaymentMethodOptionsModel;
	/** A signature question: drawn, typed, or either (both listed). Drawn when absent. */
	signatureModes?: Array<FormSignatureMode>;
	/** A consent question's terms: in a scrolling box (default) or a dialog its link opens. */
	termsDisplay?: 'box' | 'dialog';
	choices?: FormComputedChoices;
	calculation?: FormCalculation;
	/**
	 * What this field means to a processing profile (sent-form-processing.ts): the
	 * agreement type, the effective date, the equipment rows… Lets the processing screen
	 * find its inputs without depending on a field's key.
	 */
	role?: string;
}


/**
 * PD-13768 — the processing post-actions a sent form's submission offers (only on tenants
 * with the merchandiser capability). Null offers none.
 */
export const FORM_PROCESSING_PROFILES = [
	'equipment_agreement',
	'event_rental',
] as const;

export type FormProcessingProfile = (typeof FORM_PROCESSING_PROFILES)[number];

/** PD-13768 — a form's sent-form settings (`form.sendSettings`). */
export interface FormSendSettingsModel {
	/** Offered under Send form on a client or lead. */
	sendable: boolean;
	/** How long a sent link works, in days. */
	linkExpiryDays: number;
	/** The person processing the submission signs after the customer. */
	countersign: boolean;
	processingProfile: FormProcessingProfile | null;
}

export const DEFAULT_FORM_SEND_SETTINGS: Readonly<FormSendSettingsModel> = {
	sendable: false,
	linkExpiryDays: 30,
	countersign: false,
	processingProfile: null,
};
